/**
 * RecallBanner — FE #198 Sub 2.
 *
 * Mounted at the top of `ItemDetailScreen` (above the photo carousel)
 * when the viewer is the current custodian AND the item has an active
 * recall pending. Replaces the situation where the borrower received
 * the push + email but the in-app UI gave zero recall signal.
 *
 * Two variants — full (ItemDetail) and compact (Dashboard banner
 * stack). Both branch off the same `active_recall` shape; copy +
 * spacing differ.
 *
 * Acknowledgement
 * ---------------
 * The primary CTA fires `api.acknowledgeRecall(itemId)` — a single
 * server call that flips custody back to the owner, marks the
 * RECALL borrow_requests row FULFILLED, writes a RECALL_ACK custody
 * event, and emails the owner. Used to be a two-step "return then
 * confirm"; backend#147 collapsed it.
 *
 * Safety guards
 * -------------
 * - Renders nothing when `active_recall` is null/missing — keeps the
 *   layout identical to today for the (large) majority of items.
 * - Pending state on the CTA prevents double-submission.
 * - Failure path surfaces an error toast + keeps the banner visible
 *   so the user can retry.
 */

import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';

import { fpColors } from '../../../styles/theme';
import { api } from '../../../api/remembrApi';
import type { ActiveRecallInfo } from '../../../api/types';

export interface RecallBannerProps {
  itemId: number;
  recall: ActiveRecallInfo;
  /** Optional item name for tap-thru navigation context (compact variant). */
  itemName?: string;
  /** Compact = Dashboard "you hold N items with active recall" rendering.
   *  Full = ItemDetail's role=alert urgent block with [Acknowledge] CTA. */
  variant?: 'full' | 'compact';
  /** Called on successful ack so the parent can refetch fresh state. */
  onAcknowledged?: () => void;
  /** Compact variant only — tap → ItemDetail. */
  onPress?: () => void;
}

export const RecallBanner: React.FC<RecallBannerProps> = ({
  itemId,
  recall,
  itemName,
  variant = 'full',
  onAcknowledged,
  onPress,
}) => {
  const [acknowledging, setAcknowledging] = useState(false);

  // Backend shipped a slightly different shape than the spec —
  // `message` instead of `reason`, `requester_id` instead of
  // `requester_user_id`, no `requester_name` denormalization, and
  // `expires_at` instead of `expected_by`. Read either with the
  // BE-actual shape preferred, falling back to spec'd names for
  // forward-compat in case the BE adds the denormalized name later.
  const reason = ((recall.reason ?? (recall as any).message ?? '') as string).trim();
  const requester = ((recall.requester_name ?? 'The owner') as string).trim();
  const timeAgo = formatTimeAgo(recall.created_at);
  // Only pull `expected_by` (the spec's owner-set SLA). The BE-shipped
  // `expires_at` is the borrow_requests row-timeout (+7d), not a deadline
  // — surfacing it would soften urgency ("by next Tuesday" instead of
  // "ASAP"), so we ignore it here and let the banner default to "ASAP".
  const expectedBy = recall.expected_by
    ? formatExpectedBy(recall.expected_by)
    : null;

  const handleAcknowledge = async () => {
    if (acknowledging) return;
    setAcknowledging(true);
    try {
      await api.acknowledgeRecall(itemId);
      Toast.show({
        type: 'success',
        text1: 'Recall acknowledged',
        text2: `${itemName ?? 'Item'} returned to ${requester}`,
      });
      onAcknowledged?.();
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: "Couldn't acknowledge",
        text2:
          (typeof err === 'string'
            ? err
            : err?.response?.data?.detail || err?.message) ??
          'Please try again.',
      });
    } finally {
      setAcknowledging(false);
    }
  };

  if (variant === 'compact') {
    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`Active recall — ${requester} needs ${itemName ?? 'an item'} back. Tap to respond.`}
        // @ts-ignore — RN Web only; lets screen readers announce as alert.
        role="alert"
        style={({ pressed }) => [
          styles.compact,
          pressed && { opacity: 0.85 },
        ]}
        testID={`recall-banner-compact-${itemId}`}
      >
        <View style={styles.compactIconWrap}>
          <MaterialIcons name="priority-high" size={20} color={fpColors.error} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.compactTitle}>
            {`${requester} needs ${itemName ?? 'an item'} back`}
          </Text>
          <Text style={styles.compactSubtitle} numberOfLines={1}>
            {expectedBy ?? 'ASAP'}
            {timeAgo ? ` · ${timeAgo}` : ''}
          </Text>
        </View>
        <MaterialIcons
          name="chevron-right"
          size={20}
          color={fpColors.onErrorContainer ?? fpColors.error}
        />
      </Pressable>
    );
  }

  return (
    <View
      style={styles.full}
      accessible
      accessibilityRole="text"
      // @ts-ignore — RN Web announces the banner immediately.
      role="alert"
      accessibilityLabel={`Recall requested by ${requester}. Reason: ${reason || 'Owner requested return'}. Acknowledge & return.`}
      testID={`recall-banner-${itemId}`}
    >
      <View style={styles.header}>
        <View style={styles.fullIconWrap}>
          <MaterialIcons name="priority-high" size={20} color={fpColors.error} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>RECALL REQUESTED</Text>
          <Text style={styles.title}>{`${requester} needs this back ${expectedBy ?? 'ASAP'}`}</Text>
        </View>
        {timeAgo ? <Text style={styles.timestamp}>{timeAgo}</Text> : null}
      </View>

      {reason ? (
        <View style={styles.reasonRow}>
          <Text style={styles.reasonLabel}>Reason</Text>
          <Text style={styles.reasonText}>"{reason}"</Text>
        </View>
      ) : null}

      <Pressable
        onPress={handleAcknowledge}
        disabled={acknowledging}
        accessibilityRole="button"
        accessibilityLabel="Acknowledge recall and return item"
        style={({ pressed }) => [
          styles.cta,
          (pressed || acknowledging) && { opacity: 0.85 },
        ]}
        testID={`recall-banner-ack-${itemId}`}
      >
        <MaterialIcons
          name="check-circle"
          size={16}
          color={fpColors.onError ?? '#FFFFFF'}
        />
        <Text style={styles.ctaLabel}>
          {acknowledging ? 'Returning…' : 'Acknowledge & return'}
        </Text>
      </Pressable>
    </View>
  );
};

/** Tiny self-contained time-ago renderer. Mirrors the one in
 *  PendingRequestCard so the two surfaces produce identical phrasing
 *  ("just now", "5m ago", "3h ago", "2d ago", "Apr 28"). */
function formatTimeAgo(iso: string | undefined | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/** Format the owner-set SLA for the urgent subtitle. We lean
 *  understatement: "by tomorrow 9am" feels less aggressive than
 *  "WITHIN 16 HOURS!!!" and reads better in the audit log too. */
function formatExpectedBy(iso: string): string | null {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const sameDay =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow =
      d.getDate() === tomorrow.getDate() &&
      d.getMonth() === tomorrow.getMonth() &&
      d.getFullYear() === tomorrow.getFullYear();
    const time = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    if (sameDay) return `by today ${time}`;
    if (isTomorrow) return `by tomorrow ${time}`;
    const datePart = d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    return `by ${datePart} ${time}`;
  } catch {
    return null;
  }
}

const styles = StyleSheet.create({
  full: {
    backgroundColor: (fpColors as any).errorContainer ?? '#FFDAD6',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: fpColors.error,
    gap: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  fullIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: (fpColors as any).surface ?? '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    letterSpacing: 0.6,
    color: fpColors.error,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    marginTop: 2,
  },
  timestamp: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
  },
  reasonRow: {
    gap: 2,
    paddingLeft: 44, // align under the title (32 icon + 12 gap)
  },
  reasonLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    letterSpacing: 0.4,
    color: fpColors.onSurfaceVariant,
  },
  reasonText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurface,
    fontStyle: 'italic',
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 11,
    borderRadius: 999,
    backgroundColor: fpColors.error,
    marginTop: 2,
  },
  ctaLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    letterSpacing: 0.3,
    color: (fpColors as any).onError ?? '#FFFFFF',
  },

  compact: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 14,
    backgroundColor: (fpColors as any).errorContainer ?? '#FFDAD6',
    borderWidth: 1,
    borderColor: fpColors.error,
    marginBottom: 12,
  },
  compactIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: (fpColors as any).surface ?? '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  compactSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
});

export default RecallBanner;
